import { useState } from 'react';
import { calculateMonthlyPayment, formatCurrency } from '../utils/mortgageCalculations';
import { calculateEscrow } from '../utils/propertyDataService';

function LoanComparison({ currentLoan, comparisons, setComparisons }) {
    // Local state for the form only
    const [showForm, setShowForm] = useState(false);

    const [formData, setFormData] = useState({
        name: '',
        homePrice: 500000,
        downPayment: 100000,
        downPaymentPercent: 20,
        interestRate: 6.5,
        loanYears: 30,
        zipCode: '90210'
    });

    const handleAddLoan = () => {
        const loanAmount = formData.homePrice - formData.downPayment;
        // Recalculate percent to ensure precision if needed, or use state
        const downPaymentPercent = formData.downPaymentPercent;
        const monthlyPI = calculateMonthlyPayment(loanAmount, formData.interestRate, formData.loanYears);
        const escrowData = calculateEscrow(formData.homePrice, formData.zipCode, loanAmount, downPaymentPercent);
        const totalMonthly = monthlyPI + escrowData.total;

        const newLoan = {
            ...formData,
            loanAmount,
            downPaymentPercent,
            monthlyPI,
            escrow: escrowData.total,
            totalMonthly,
            totalPaid: totalMonthly * formData.loanYears * 12,
            totalInterest: (monthlyPI * formData.loanYears * 12) - loanAmount
        };

        setComparisons([...comparisons, newLoan]);

        setShowForm(false);
        setFormData({
            name: '',
            homePrice: 500000,
            downPayment: 100000,
            downPaymentPercent: 20,
            interestRate: 6.5,
            loanYears: 30,
            zipCode: '90210'
        });
    };

    const handleRemoveLoan = (index) => {
        setComparisons(comparisons.filter((_, i) => i !== index));
    };

    return (
        <div className="card" style={{ paddingLeft: 'var(--spacing-lg)', paddingRight: 'var(--spacing-lg)' }}>
            <div className="card-header">
                <h3 className="card-title">Loan Comparison</h3>
                <button
                    className="btn btn-primary"
                    onClick={() => setShowForm(!showForm)}
                >
                    {showForm ? 'Cancel' : '+ Add Loan Scenario'}
                </button>
            </div>

            <p style={{ color: 'var(--text-secondary)', marginBottom: 'var(--spacing-md)' }}>
                Compare different loan scenarios side-by-side to find the best option for you.
            </p>

            {showForm && (
                <div className="card" style={{ background: 'var(--bg-tertiary)', marginBottom: 'var(--spacing-md)' }}>
                    <h4 style={{ marginBottom: 'var(--spacing-md)' }}>Add Loan Scenario</h4>

                    <div className="grid grid-2">
                        <div className="form-group">
                            <label className="form-label">Scenario Name</label>
                            <input
                                type="text"
                                className="form-input"
                                value={formData.name}
                                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                                placeholder="e.g., 15-year option"
                            />
                        </div>

                        <div className="form-group">
                            <label className="form-label">Home Price</label>
                            <div className="input-group">
                                <span className="input-prefix">$</span>
                                <input
                                    type="number"
                                    className="form-input"
                                    value={formData.homePrice}
                                    onChange={(e) => {
                                        const val = parseFloat(e.target.value) || 0;
                                        setFormData({
                                            ...formData,
                                            homePrice: val,
                                            // Keep percentage constant, update amount
                                            downPayment: (val * formData.downPaymentPercent / 100)
                                        });
                                    }}
                                />
                            </div>
                        </div>

                        <div className="form-group">
                            <label className="form-label">Down Payment</label>
                            <div className="grid grid-2" style={{ gap: '0.5rem' }}>
                                <div className="input-group">
                                    <span className="input-prefix">$</span>
                                    <input
                                        type="number"
                                        className="form-input"
                                        value={formData.downPayment}
                                        onChange={(e) => {
                                            const val = parseFloat(e.target.value) || 0;
                                            setFormData({
                                                ...formData,
                                                downPayment: val,
                                                downPaymentPercent: (val / formData.homePrice * 100)
                                            });
                                        }}
                                    />
                                </div>
                                <div className="input-group">
                                    <input
                                        type="number"
                                        className="form-input"
                                        value={formData.downPaymentPercent ? parseFloat(formData.downPaymentPercent.toFixed(3)) : ''}
                                        onChange={(e) => {
                                            const val = parseFloat(e.target.value) || 0;
                                            setFormData({
                                                ...formData,
                                                downPaymentPercent: val,
                                                downPayment: (formData.homePrice * val / 100)
                                            });
                                        }}
                                    />
                                    <span className="input-suffix">%</span>
                                </div>
                            </div>
                        </div>

                        <div className="form-group">
                            <label className="form-label">Interest Rate</label>
                            <div className="input-group">
                                <input
                                    type="number"
                                    className="form-input"
                                    value={formData.interestRate}
                                    onChange={(e) => setFormData({ ...formData, interestRate: parseFloat(e.target.value) })}
                                    step="0.125"
                                />
                                <span className="input-suffix">%</span>
                            </div>
                        </div>

                        <div className="form-group">
                            <label className="form-label">Loan Term (Years)</label>
                            <select
                                className="form-select"
                                value={formData.loanYears}
                                onChange={(e) => setFormData({ ...formData, loanYears: parseInt(e.target.value) })}
                            >
                                <option value="15">15 Years</option>
                                <option value="20">20 Years</option>
                                <option value="25">25 Years</option>
                                <option value="30">30 Years</option>
                            </select>
                        </div>

                        <div className="form-group">
                            <label className="form-label">Zip Code</label>
                            <input
                                type="text"
                                className="form-input"
                                value={formData.zipCode}
                                onChange={(e) => setFormData({ ...formData, zipCode: e.target.value })}
                                maxLength="5"
                            />
                        </div>
                    </div>

                    <button
                        className="btn btn-primary"
                        onClick={handleAddLoan}
                        disabled={!formData.name}
                    >
                        Add to Comparison
                    </button>
                </div>
            )}

            {comparisons.length === 0 ? (
                <div className="alert alert-info">
                    <strong>No loan scenarios added yet.</strong> Click "Add Loan Scenario" to start comparing different options.
                </div>
            ) : (
                <div className="table-container">
                    <table className="loan-comparison-table">
                        <thead>
                            <tr>
                                <th title="Scenario Name">Scenario</th>
                                <th title="Loan Amount">Loan Amt</th>
                                <th title="Interest Rate">Rate</th>
                                <th title="Loan Term">Term</th>
                                <th title="Monthly Principal & Interest">Mo. P&I</th>
                                <th title="Total Monthly Payment">Total Mo.</th>
                                <th title="Total Interest Paid">Tot. Int.</th>
                                <th title="Total Amount Paid">Tot. Paid</th>
                                <th title="Remove Scenario">Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {comparisons.map((loan, index) => (
                                <tr key={index}>
                                    <td><strong>{loan.name}</strong></td>
                                    <td>{formatCurrency(loan.loanAmount)}</td>
                                    <td>{loan.interestRate}%</td>
                                    <td>{loan.loanYears} years</td>
                                    <td>{formatCurrency(loan.monthlyPI)}</td>
                                    <td>{formatCurrency(loan.totalMonthly)}</td>
                                    <td>{formatCurrency(loan.totalInterest)}</td>
                                    <td>{formatCurrency(loan.totalPaid)}</td>
                                    <td>
                                        <button
                                            className="btn btn-secondary"
                                            onClick={() => handleRemoveLoan(index)}
                                            style={{ padding: '0.5rem 1rem' }}
                                        >
                                            Remove
                                        </button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

            {comparisons.length > 1 && (
                <div className="alert alert-info" style={{ marginTop: 'var(--spacing-md)' }}>
                    <strong>Tip:</strong> The loan with the lowest total interest paid will save you the most money over time,
                    but consider whether the higher monthly payment fits your budget.
                </div>
            )}
        </div>
    );
}

export default LoanComparison;
